// Right-rail inspector — renders the pinned topic's full context.
//
// Lifted from H3-BFSI Lens vendor/dashboard.js (renderTrendInspector +
// renderMatrixRationale). Same shape: definition / per-metric scores with
// justifications / per-business-line relevance bars / source attribution.

import type {
  BusinessLine,
  Metric,
  TopicWithContext,
} from "../lib/types.ts";

const escHtml = (s: string | null | undefined) =>
  String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );

export interface InspectorCallbacks {
  onUnpin: () => void;
}

export function renderTopicInspector(
  body: HTMLElement,
  ctx: TopicWithContext,
  metrics: Metric[],
  businessLines: BusinessLine[],
  cb: InspectorCallbacks,
): () => void {
  const { topic } = ctx;

  const memberships = ctx.memberships
    .map((m) => `<span class="chip">${escHtml(m.category.name)} › ${escHtml(m.trend.name)}${m.is_primary ? "" : " (sec.)"}</span>`)
    .join(" ");

  const v1 = metrics.filter((m) => m.metric_set === "v1").sort((a, b) => a.sort_order - b.sort_order);
  const metricRows = v1.map((m) => {
    const s = ctx.scores[m.key];
    if (!s) {
      return `<tr><td>${escHtml(m.name)}</td><td colspan="2" class="muted">no score yet</td></tr>`;
    }
    const chip = s.score
      ? `<span class="score-chip score-${s.score}${s.estimated ? " est" : ""}">${s.score}</span>`
      : `<span class="muted">—</span>`;
    return `<tr>
      <td>${escHtml(m.name)}${s.estimated ? ' <span class="est-tag" title="AI estimate">est</span>' : ""}</td>
      <td class="just">${escHtml(s.justification ?? "")}</td>
      <td>${chip}</td>
    </tr>`;
  }).join("");

  const relevanceBars = businessLines.map((bl) => {
    const r = ctx.relevance[bl.key];
    const score = r?.score ?? 0;
    const pct = (score / 3) * 100;
    return `<div class="rel-row">
      <span class="rel-label">${escHtml(bl.name)}</span>
      <span class="rel-track"><span class="rel-fill" style="width:${pct}%"></span></span>
      <span class="rel-val">${score || "—"}</span>
    </div>`;
  }).join("");

  body.innerHTML = `
    <div class="insp-header">
      <span class="kind-chip">${topic.h3_thesis_flag ? "H3 Thesis Topic" : "Topic"}</span>
      <button class="unpin-btn" id="unpinBtn">Unpin</button>
    </div>
    <h2 class="insp-title">${escHtml(topic.name)}</h2>
    ${topic.ai_drafted ? '<div class="ai-flag">⚠ AI-drafted; awaits editor review</div>' : ""}
    <div class="insp-memberships">${memberships}</div>

    ${topic.definition ? `
      <section class="insp-section">
        <h3>Definition</h3>
        <p>${escHtml(topic.definition)}</p>
      </section>` : ""}
    ${topic.tipping_point ? `
      <section class="insp-section">
        <h3>Tipping point</h3>
        <p>${escHtml(topic.tipping_point)}</p>
      </section>` : ""}
    ${topic.milestone_signal ? `
      <section class="insp-section">
        <h3>Milestone signal</h3>
        <p>${escHtml(topic.milestone_signal)}</p>
      </section>` : ""}

    <section class="insp-section">
      <h3>Readiness scores (V1)</h3>
      <table class="metric-table">
        <thead><tr><th>Metric</th><th>Why</th><th>Score</th></tr></thead>
        <tbody>${metricRows}</tbody>
      </table>
      ${ctx.readiness?.avg_readiness_v1 != null
        ? `<p class="rollup">Avg readiness: <strong>${ctx.readiness.avg_readiness_v1.toFixed(2)}</strong> across ${ctx.readiness.v1_metric_count} metrics</p>`
        : ""}
    </section>

    <section class="insp-section">
      <h3>Business-line relevance</h3>
      <div class="rel-grid">${relevanceBars}</div>
    </section>

    ${topic.search_terms?.length ? `
      <section class="insp-section">
        <h3>Search terms</h3>
        <p class="muted">Used by adapters to query each metric's source.</p>
        <div class="search-terms">${topic.search_terms.map((t) => `<span class="chip">${escHtml(t)}</span>`).join(" ")}</div>
      </section>` : ""}

    <section class="insp-section share-section">
      <button class="share-link-btn" id="copyShareLink">Copy share link</button>
      <a class="share-link" href="#/topic/${encodeURIComponent(topic.slug)}">Open share view →</a>
    </section>
  `;

  const unpin = body.querySelector<HTMLButtonElement>("#unpinBtn");
  unpin?.addEventListener("click", cb.onUnpin);

  // Copy-link button — uses navigator.clipboard if available, falls back to
  // a select+execCommand path for older browsers (mostly relevant on iOS
  // where clipboard API requires HTTPS + user gesture, both of which we have).
  const copy = body.querySelector<HTMLButtonElement>("#copyShareLink");
  copy?.addEventListener("click", async () => {
    const url = `${location.origin}/#/topic/${encodeURIComponent(topic.slug)}`;
    try {
      await navigator.clipboard.writeText(url);
      copy.textContent = "Copied ✓";
      setTimeout(() => { copy.textContent = "Copy share link"; }, 1500);
    } catch {
      // Fallback: prompt with the URL pre-selected so the user can copy manually.
      prompt("Copy this URL:", url);
    }
  });

  return () => { body.innerHTML = ""; };
}
